/**
 * Reactivation-population autotest resolver for the logged-in FocusedNav.
 *
 * Sister of AutotestFocusedNav (the activation/cold-non-premium resolver). This
 * one targets the FORMERLY-PREMIUM (churned) population: users who see the
 * "Reactivate Premium" CTA. Bucketed server-side in FocusedNavPresenter as
 * `navReactivationArm` (the nav is SSR-prerendered, so a client bucket would
 * flash) and resolved here to surface overrides on the reactivate CTA.
 *
 * The test launched as an A/A via the AT-prep plumbing PR (REACT_ARMS empty,
 * validating the resolver/delivery path); this populates REACT_ARMS with the 3
 * treatments — a visual discount badge, personalized "N new mentions since you
 * left", and warm winback copy — flipping it to real treatment with no
 * relaunch/re-bucket. Control still resolves to today's reactivate CTA exactly.
 * Test: nav_reactivation_2026_q2_v1. The cohort is ~8x smaller than the trial CTA,
 * so the slate is intentionally small (3 arms) for power.
 */

// === Arms (control + 3) ===

export type ReactArm =
  | 'control'
  | 'v_react_discount' // surface the real discount as a visual badge
  | 'v_react_mentions' // personalized "N new mentions since you left" (real count)
  | 'v_react_winback'; // warm winback copy

/** Resolved arm. `undefined` = not bucketed (premium / cold) → CONTROL. */
export type ReactResolvedArm = ReactArm | undefined;

// === Render-time inputs ===

export type ReactSurfaceArgs = {
  isMobileNav: boolean;
  /** The churned reactivation offer type, or null when there's no offer. */
  reactivateOffer: 'discount' | 'price' | null;
  /** The REAL formatted offer value (e.g. "50%", "$4.99"), or null. */
  reactivateValue: string | null;
  /** REAL unseen-mentions accrued since the user churned, or null when none
   * (Premium::ChurnedUser#component_props -> mentions_since_last_premium). */
  reactivateMentions: number | null;
};

// === Surfaces ===

export type ReactSurfaces = {
  /** Override copy for the churned "Reactivate Premium" CTA. `null` = today's
   * offer-derived text (CONTROL). */
  reactivateLabel: string | null;
  /** Render the real discount as a distinct visual badge/pill on the reactivate
   * CTA (vs control's inline "X OFF" text). false = CONTROL. */
  reactivateDiscountBadge: boolean;
};

/** CONTROL is the single source of every default — today's shipped reactivate CTA. */
export const REACT_CONTROL: ReactSurfaces = {
  reactivateLabel: null,
  reactivateDiscountBadge: false,
};

// === ARMS ===
type ReactArmSpec = (args: ReactSurfaceArgs) => Partial<ReactSurfaces>;

const REACT_ARMS: Partial<Record<ReactArm, ReactArmSpec>> = {
  // Discount as a VISUAL badge (not copy): surface the real discount value as a
  // distinct pill. Discount offers only; price offers / no offer fall back to
  // control (no fabrication).
  v_react_discount: ({ reactivateOffer }) =>
    reactivateOffer === 'discount' ? { reactivateDiscountBadge: true } : {},
  // Mentions-themed, two-state — always treats (never reverts to control):
  // a concrete personalized count when there are new mentions since churn (the
  // REAL mentions_since_last_premium), else a citations value-prop for users with
  // none. Both keep the arm on-theme and distinct from control.
  v_react_mentions: ({ reactivateMentions }) =>
    reactivateMentions
      ? { reactivateLabel: `${reactivateMentions} new mentions since you left` }
      : { reactivateLabel: "See who's citing your work" },
  // Warm winback framing instead of a transactional "reactivate".
  v_react_winback: () => ({ reactivateLabel: 'Welcome back to Premium' }),
};

/**
 * Resolve the reactivation surfaces for an arm. Unknown/undefined arm → CONTROL.
 * Always spreads over CONTROL so every field has a value.
 */
export function resolveReactivationSurfaces(
  arm: ReactResolvedArm,
  args: ReactSurfaceArgs,
): ReactSurfaces {
  const spec = arm !== undefined ? REACT_ARMS[arm] : undefined;
  return { ...REACT_CONTROL, ...(spec ? spec(args) : {}) };
}
